// An ISA needs a General Investment Account (SEP-293.7).
//
// The firm opens an ISA only beside a General Investment Account of the same
// client. The bank holds the rule in three places, and this module names each
// value once:
//
//   - The journey: a press on ISA also chooses the General Investment Account,
//     and the client cannot remove the General Investment Account while the
//     ISA stays chosen (`impliedOptions` on `account_types`,
//     `chooseClientApplicationOption`). A press that removes the ISA keeps the
//     General Investment Account. The journey prints the notice of the firm
//     under the control while the ISA is chosen.
//   - The server: a form rule refuses an ISA with no General Investment
//     Account, on a save and on the submission.
//   - A draft: a stored draft that holds the ISA alone gets the General
//     Investment Account when the server reads it. The client then sees both
//     ticked (`clientApplicationIsaWithGeneralInvestment`).

import type { ClientApplicationFormRule } from './client-application-questions';

/** The question of the account types. */
export const CLIENT_APPLICATION_ACCOUNT_TYPES_ID = 'account_types';

/** The option label of the ISA. */
export const CLIENT_APPLICATION_ISA_OPTION = 'ISA';

/** The option label of the General Investment Account. */
export const CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION = 'Individual General Investment';

/** The firm's own notice under the account type while the ISA is chosen. */
export const CLIENT_APPLICATION_ISA_NEEDS_GIA_NOTICE =
    'Please note - to open an ISA with us, you must also open a General Investment Account';

/** The message of the form rule. */
export const CLIENT_APPLICATION_ISA_NEEDS_GIA_MESSAGE =
    'To open an ISA with us, you must also open a General Investment Account.';

/** The account types of one answer map, as a list. An older single string is a list of one. */
function accountTypes(answers: Readonly<Record<string, unknown>>): readonly unknown[] {
    const value = answers[CLIENT_APPLICATION_ACCOUNT_TYPES_ID];
    if (Array.isArray(value)) return value;
    return typeof value === 'string' && value !== '' ? [value] : [];
}

/** Whether the answers choose the ISA and no General Investment Account. */
export function clientApplicationIsaWithoutGeneralInvestment(
    answers: Readonly<Record<string, unknown>>,
): boolean {
    const chosen = accountTypes(answers);
    return (
        chosen.includes(CLIENT_APPLICATION_ISA_OPTION) &&
        !chosen.includes(CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION)
    );
}

/**
 * The account types of a stored draft, with the General Investment Account
 * added when the draft holds the ISA alone. The order is the order of the
 * bank: the General Investment Account comes first. Any other value comes
 * back unchanged.
 */
export function clientApplicationIsaWithGeneralInvestment(value: unknown): unknown {
    if (!Array.isArray(value)) return value;
    if (
        !value.includes(CLIENT_APPLICATION_ISA_OPTION) ||
        value.includes(CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION)
    ) {
        return value;
    }
    return [CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION, ...value];
}

/** The form rule: an ISA needs a General Investment Account. */
export const CLIENT_APPLICATION_ISA_NEEDS_GIA_RULE: ClientApplicationFormRule = {
    id: 'isa_needs_general_investment_account',
    check: (answers) =>
        clientApplicationIsaWithoutGeneralInvestment(answers)
            ? [
                  {
                      questionId: CLIENT_APPLICATION_ACCOUNT_TYPES_ID,
                      message: CLIENT_APPLICATION_ISA_NEEDS_GIA_MESSAGE,
                  },
              ]
            : [],
};
